import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Heart, Search, User, Globe, PlusCircle, Home, Grid3X3, HeartIcon, LogIn, UserPlus, LogOut, Star, Building2 } from 'lucide-react';
import { useFavoriteStore, useAuthStore, useUserAuthStore, useSavedSearchStore } from '@/store';
import { GlassFilter, MenuBar } from '@/components/ui';
import { SavedSearchesModal } from '@/components/search';
import { NotificationsBell } from '@/components/notifications';

export function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSavedSearchesOpen, setIsSavedSearchesOpen] = useState(false);
  const favoriteIds = useFavoriteStore((state) => state.favoriteIds);
  const { isAuthenticated: isAdminAuth } = useAuthStore();
  const { isAuthenticated: isUserAuth, user, logout: userLogout } = useUserAuthStore();
  const { savedSearches, fetchSavedSearches } = useSavedSearchStore();
  const hasFavorites = favoriteIds.size > 0;
  const hasSavedSearches = savedSearches.length > 0;

  // Fetch saved searches when user is authenticated
  useEffect(() => {
    if (isUserAuth) {
      fetchSavedSearches();
    }
  }, [isUserAuth, fetchSavedSearches]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/search', label: t('nav.search') },
    { to: '/brands', label: t('nav.brands', 'Brands') },
    { to: '/dealers', label: t('nav.dealers', 'Dealers') },
    { to: '/favorites', label: t('nav.favorites') },
  ];

  // Custom Favorites icon:
  // - On favorites page: filled red heart
  // - On other pages with favorites: red outline heart
  // - No favorites: default gray outline
  const isOnFavoritesPage = location.pathname === '/favorites';
  const FavoritesIcon = ({ className }: { className?: string }) => (
    <HeartIcon
      className={`${className} ${hasFavorites ? 'text-red-500' : ''}`}
      fill={hasFavorites && isOnFavoritesPage ? 'currentColor' : 'none'}
    />
  );

  // Menu items for GlowMenu
  const menuItems = [
    {
      icon: Home,
      label: t('nav.home'),
      href: '/',
      gradient: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.08) 50%, rgba(29,78,216,0) 100%)',
      iconColor: 'text-blue-500',
    },
    {
      icon: Search,
      label: t('nav.search'),
      href: '/search',
      gradient: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.08) 50%, rgba(29,78,216,0) 100%)',
      iconColor: 'text-blue-500',
    },
    {
      icon: Grid3X3,
      label: t('nav.brands', 'Brands'),
      href: '/brands',
      gradient: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.08) 50%, rgba(29,78,216,0) 100%)',
      iconColor: 'text-blue-500',
    },
    {
      icon: Building2,
      label: t('nav.dealers', 'Dealers'),
      href: '/dealers',
      gradient: 'radial-gradient(circle, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.08) 50%, rgba(4,120,87,0) 100%)',
      iconColor: 'text-emerald-500',
    },
    {
      icon: FavoritesIcon,
      label: t('nav.favorites'),
      href: '/favorites',
      gradient: 'radial-gradient(circle, rgba(239,68,68,0.2) 0%, rgba(220,38,38,0.08) 50%, rgba(185,28,28,0) 100%)',
      iconColor: 'text-red-500',
    },
  ];

  // Get active menu item based on current path
  const getActiveItem = () => {
    const currentPath = location.pathname;
    const item = menuItems.find(item => item.href === currentPath || (item.href !== '/' && currentPath.startsWith(`${item.href}/`)));
    return item?.label || '';
  };

  const handleMenuClick = (_label: string, href: string) => {
    navigate(href);
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ku', name: 'کوردی' },
    { code: 'ar', name: 'العربية' },
  ];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      {/* Glass Filter SVG - needed for glass effects */}
      <GlassFilter />

      <div className="max-w-[1560px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 sm:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/images/logo.png"
              alt="KurdaCars"
              className="h-10 sm:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation - Glow Menu */}
          <nav className="hidden md:flex items-center gap-3">
            {/* Glow Menu Bar */}
            <MenuBar
              items={menuItems}
              activeItem={getActiveItem()}
              onItemClick={handleMenuClick}
            />

            {/* Sell Car Button - Amber */}
            <Link
              to="/sell"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900 text-sm font-semibold rounded-full hover:from-amber-500 hover:to-yellow-600 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-amber-500/25"
            >
              <PlusCircle className="w-4 h-4" />
              {t('nav.sellCar')}
            </Link>
          </nav>

          {/* Right Side Icons */}
          <div className="flex items-center gap-3">
            {/* Search Icon - Mobile */}
            <Link
              to="/search"
              className="md:hidden p-2 text-gray-600 hover:text-primary-600 transition-colors"
            >
              <Search className="w-5 h-5" />
            </Link>

            {/* Favorites with Badge */}
            <Link
              to="/favorites"
              className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors md:hidden"
            >
              <Heart className="w-5 h-5" />
              {favoriteIds.size > 0 && (
                <span className="absolute -top-1 -end-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {favoriteIds.size > 9 ? '9+' : favoriteIds.size}
                </span>
              )}
            </Link>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute end-0 mt-2 w-36 bg-white/90 backdrop-blur-md rounded-xl shadow-lg border border-white/50 py-1 z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full px-4 py-2 text-start text-sm hover:bg-white/50 ${
                          i18n.language === lang.code
                            ? 'text-primary-600 font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Saved Searches Icon - Only for authenticated users */}
            {isUserAuth && (
              <button
                onClick={() => setIsSavedSearchesOpen(true)}
                className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative"
                title={t('savedSearches.title')}
              >
                <Star
                  className={`w-5 h-5 ${hasSavedSearches ? 'text-amber-500' : ''}`}
                  fill={hasSavedSearches ? 'currentColor' : 'none'}
                />
              </button>
            )}

            {/* Notifications Bell - Only for authenticated users */}
            {isUserAuth && <NotificationsBell />}

            {/* User Account Menu */}
            <div className="relative hidden sm:block">
              {isUserAuth ? (
                <>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-sm font-medium max-w-[100px] truncate">
                      {user?.firstName || user?.email?.split('@')[0]}
                    </span>
                  </button>
                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute end-0 mt-2 w-48 bg-white/90 backdrop-blur-md rounded-xl shadow-lg border border-white/50 py-1 z-50"
                      >
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                        {isAdminAuth && (
                          <Link
                            to="/admin"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {t('nav.admin', 'Admin Panel')}
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            userLogout();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-start px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('nav.logout', 'Logout')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <LogIn className="w-4 h-4" />
                    {t('nav.login', 'Login')}
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    {t('nav.register', 'Register')}
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t bg-white/90 backdrop-blur-md"
          >
            <nav className="px-4 py-3 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 rounded-lg text-sm font-medium ${
                    location.pathname === link.to || (link.to !== '/' && location.pathname.startsWith(`${link.to}/`))
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-primary-600 hover:bg-gray-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Sell Car Link in Mobile */}
              <Link
                to="/sell"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-400 to-yellow-500 text-gray-900"
              >
                <PlusCircle className="w-4 h-4" />
                {t('nav.sellCar')}
              </Link>
              {isUserAuth ? (
                <>
                  <div className="px-3 py-2 border-t border-gray-200 mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  {isAdminAuth && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
                    >
                      {t('nav.admin', 'Admin Panel')}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      userLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-start px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    {t('nav.logout', 'Logout')}
                  </button>
                </>
              ) : (
                <div className="flex gap-2 pt-2 border-t border-gray-200 mt-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex-1 text-center px-3 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-100"
                  >
                    {t('nav.login', 'Login')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex-1 text-center px-3 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
                  >
                    {t('nav.register', 'Register')}
                  </Link>
                </div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Saved Searches Modal */}
      <SavedSearchesModal
        isOpen={isSavedSearchesOpen}
        onClose={() => setIsSavedSearchesOpen(false)}
      />
    </header>
  );
}
